import { z } from 'zod';

export const tokenObjectSchema = z.object({
  access_token: z.string().min(1),
  expires_in: z.number(),
  scope: z.string().optional(),
  token_type: z.string(),
  confirmed: z.boolean().optional(),
});
export type TokenObject = z.infer<typeof tokenObjectSchema>;

export const screeningStatusSchema = z.enum([
  'New',
  'Screening',
  'Waiting Room',
  'Passed',
  'Failed',
]);
export type ScreeningStatus = z.infer<typeof screeningStatusSchema>;

export const workTypeSchema = z.enum(['local', 'travel']);
export type WorkType = z.infer<typeof workTypeSchema>;

const yesNoMaybe = z.enum(['Yes', 'No', 'Maybe']);

// The hub-contract `Record` returned by /auth/me and /profile/candidate/save. Unanswered
// fields are absent (never null) except `profession`, which is always present. `.loose()`
// keeps unknown fields from failing the parse when the service grows.
export const candidateRecordSchema = z.looseObject({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string().optional(),
  zipcode: z.string().optional(),
  market: z.string().optional(),
  massMailOptOut: z.boolean().optional(),
  profession: z.object({ id: z.number().nullable(), name: z.string().nullable() }),

  cnaCertified: z.boolean().nullable().optional(),
  primaryWorkType: workTypeSchema.optional(),
  otherWorkTypeInterest: yesNoMaybe.optional(),
  mileSelection: z.number().optional(),

  stateLicensesHeld: z.array(z.string()).nullable().optional(),
  noStateLicenseNumber: z.boolean().optional(),
  licenseHit: z.boolean().nullable().optional(),
  otherStatesInterest: z.boolean().nullable().optional(),
  workAuthorized: z.boolean().nullable().optional(),
  previousWorkSetting: z.boolean().nullable().optional(),
  experience: z.string().optional(),

  desiredHours: z.string().optional(),
  desiredShifts: z.array(z.string()).optional(),
  desiredWorkday: z.array(z.string()).optional(),

  preferredContactTime: z.array(z.string()).optional(),
  preferredCommunication: z.string().optional(),

  screeningStatus: screeningStatusSchema.optional(),
  screeningStatusMessage: z.array(z.string()).optional(),
  status: z.string().optional(),
  hasRecentApplication: z.boolean().optional(),
});
export type CandidateRecord = z.infer<typeof candidateRecordSchema>;

export const lookupOptionSchema = z.object({ value: z.string(), label: z.string() });
export type LookupOption = z.infer<typeof lookupOptionSchema>;

export const LOOKUP_NAMES = [
  'profession',
  'stateLicensesHeld',
  'experience',
  'desiredHours',
  'desiredShifts',
  'desiredWorkday',
  'preferredContactTime',
  'preferredCommMethod',
] as const;
export type LookupName = (typeof LOOKUP_NAMES)[number];

export const lookupsSchema = z.record(z.string(), z.array(lookupOptionSchema));
export type Lookups = Partial<Record<LookupName, LookupOption[]>>;

export const marketSchema = z.object({
  zipcode: z.string(),
  cbsa: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  market: z.string().nullable().optional(),
});

export const messageSchema = z.looseObject({ message: z.string().optional() });
